import { css } from '@emotion/css';
import { type FormSelectionFieldProps, type FormProps, type FormTextFieldProps } from '../index';
import type { InputBaseProps } from './InputBase';
import type { FormDropdownFieldProps } from './FormDropdownField';
import type { FormPhoneFieldProps } from './FormPhoneField';
import type { CSSInterpolation, CSSObject } from '@emotion/serialize';
import { FORM_GAP } from '../../../utils/constants';
import {
  getBoxShadow,
  getBorderRadiusStyle,
  getPaddingStyle,
  getColor,
  getMediaQueryHeader,
  getGlobalStyleColor,
} from '../../../utils/styleHelpers';

import type { StylesConfig } from '../../../types/styles';

/**
 * Generates CSS class name for controlling helper text wrapper visibility.
 * Shows helper text when showHelpText prop is true, hides it otherwise.
 */
export function getHelpTextStyle(
  fieldProps:
    | InputBaseProps
    | FormTextFieldProps
    | FormDropdownFieldProps
    | FormSelectionFieldProps
    | FormPhoneFieldProps,
): string {
  return css({
    // Show only when explicitly enabled and text provided; invalid state can override via CSS
    display:
      fieldProps.showHelpText && !!(fieldProps.helpText && fieldProps.helpText.trim())
        ? 'flex'
        : 'none',
  });
}

/**
 * Generates descendant selector styles for all form fields.
 * Form component applies these styles to `.form-content` element,
 * and they cascade down to all child field elements via their classnames.
 * Returns a generated class name string from Emotion's css function.
 */
export function computeFormFieldDescendantStyles(
  props: Partial<FormProps>,
  isServerRender: boolean,
  activeGlobalStyles: StylesConfig,
): string {
  const currentBpStyles = computeFieldStyles(props, activeGlobalStyles);

  const finalStyle: CSSInterpolation = {
    ...(currentBpStyles as CSSObject),
  };

  const { mobile, tablet } = props;
  if (mobile && Object.keys(mobile).length) {
    const mobileStyles = computeFieldStyles(
      { ...props, ...mobile } as Partial<FormProps>,
      activeGlobalStyles,
    );
    finalStyle[getMediaQueryHeader(isServerRender, 'mobile')] = mobileStyles as CSSObject;
  }
  if (tablet && Object.keys(tablet).length) {
    const tabletStyles = computeFieldStyles(
      { ...props, ...tablet } as Partial<FormProps>,
      activeGlobalStyles,
    );
    finalStyle[getMediaQueryHeader(isServerRender, 'tablet')] = tabletStyles as CSSObject;
  }

  return css(finalStyle);
}

/**
 * Computes field styles for a specific breakpoint.
 * Returns plain CSS object (not a className) that can be reused for any breakpoint.
 */
function computeFieldStyles(props: Partial<FormProps>, activeGlobalStyles: StylesConfig) {
  // Resolve colors that support palette references
  const labelColor = getGlobalStyleColor(props.fieldLabelColor, activeGlobalStyles.colors);
  const inputTextColor = getGlobalStyleColor(props.fieldInputTextColor, activeGlobalStyles.colors);
  const hoverTextColor = getGlobalStyleColor(
    props.fieldInputHoverTextColor,
    activeGlobalStyles.colors,
  );
  const bgColor = getGlobalStyleColor(props.fieldInputBackgroundColor, activeGlobalStyles.colors);
  const borderColor = getGlobalStyleColor(props.fieldInputBorderColor, activeGlobalStyles.colors);
  const hoverBgColor = getGlobalStyleColor(
    props.fieldInputHoverBackgroundColor,
    activeGlobalStyles.colors,
  );
  const hoverBorderColor = getGlobalStyleColor(
    props.fieldInputHoverBorderColor,
    activeGlobalStyles.colors,
  );
  const checkboxCheckedColor = getGlobalStyleColor(
    props.fieldCheckboxCheckedColor,
    activeGlobalStyles.colors,
  );
  const invalidBgColor = getGlobalStyleColor(
    props.fieldInputInvalidBackgroundColor,
    activeGlobalStyles.colors,
  );
  const invalidBorderColor = getGlobalStyleColor(
    props.fieldInputInvalidBorderColor,
    activeGlobalStyles.colors,
  );
  const labelInvalidColor = getGlobalStyleColor(
    props.fieldLabelInvalidColor,
    activeGlobalStyles.colors,
  );
  const helperTextInvalidColor = getGlobalStyleColor(
    props.fieldHelperTextInvalidColor,
    activeGlobalStyles.colors,
  );
  return {
    // Field wrapper (outer container)
    '& .field-node': {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: `${FORM_GAP}px`,
      width: props.fieldWidth,
      maxWidth: props.fieldMaxWidth,
      minWidth: props.fieldMinWidth,
      ...getPaddingStyle(props.fieldMargin || [0, 0, 16, 0]),
      '&:has(:disabled)': {
        opacity: 0.5,
        cursor: 'not-allowed',
      },
    },

    // Submit button wrapper should align with fields spacing and sizing
    '& .form-button-node': {
      width: '100%',
      ...getPaddingStyle(props.fieldMargin || [0, 0, 16, 0]),
    },

    // Label styling
    '& .field-label': {
      fontFamily: props.fieldLabelFontFamily,
      fontSize: props.fieldLabelFontSize,
      fontWeight: props.fieldLabelFontWeight,
      color: labelColor,
      letterSpacing: `${props.fieldLabelSpacing}px`,
      lineHeight: 1.2,
    },

    // Required indicator
    '& .field-required-indicator': {
      marginLeft: '4px',
    },

    // Input wrapper (vessel with border/background)
    '& .field-input-wrapper': {
      position: 'relative' as const,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      backgroundColor: bgColor,
      ...getBorderRadiusStyle(props.fieldBorderRadius || [8, 8, 8, 8]),
      border: `${props.fieldInputBorderWidth ?? 1}px solid ${borderColor}`,
      boxShadow: getBoxShadow(props.fieldShadowType, props.fieldShadowSettings) ?? 'none',
      '&:hover:not(:has(:disabled))': {
        backgroundColor: hoverBgColor,
        borderColor: hoverBorderColor,
      },
      '&:focus-within': {
        backgroundColor: hoverBgColor,
        borderColor: hoverBorderColor,
      },
    },

    // Input element itself
    '& .field-input': {
      width: '100%',
      boxSizing: 'border-box' as const,
      fontFamily: props.fieldInputFontFamily,
      fontSize: props.fieldInputFontSize,
      fontWeight: props.fieldInputFontWeight,
      color: inputTextColor,
      letterSpacing: `${props.fieldInputSpacing}px`,
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      ...getPaddingStyle(props.fieldInputPadding || [6, 8, 6, 8]),
      '::placeholder': {
        color: inputTextColor,
      },
    },

    // Input text hover/focus color
    '& .field-input-wrapper:hover .field-input': {
      color: hoverTextColor || inputTextColor,
      '::placeholder': {
        color: hoverTextColor || inputTextColor,
      },
    },
    '& .field-input-wrapper:focus-within .field-input': {
      color: hoverTextColor || inputTextColor,
      '::placeholder': {
        color: hoverTextColor || inputTextColor,
      },
    },

    // Helper text wrapper
    '& .field-helper-text-wrapper': {
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '8px',
      marginTop: '4px',
    },
    // Helper text
    '& .field-helper-text': {
      fontFamily: props.fieldLabelFontFamily,
      fontSize: '10px',
      color: getColor(props.fieldHelperTextColor),
      textAlign: 'right' as const,
      margin: 0,
    },

    // Error icon (hidden by default)
    '& .field-input-icon': {
      position: 'absolute' as const,
      top: '50%',
      right: '8px',
      transform: 'translateY(-50%)',
      display: 'none',
      width: '16px',
      height: '16px',
      pointerEvents: 'none' as const,
      color: invalidBorderColor,
    },

    // Invalid state - label
    '& .field-node.invalid .field-label': {
      color: labelInvalidColor,
    },

    // Invalid state - input wrapper
    '& .field-node.invalid .field-input-wrapper': {
      backgroundColor: invalidBgColor,
      borderColor: invalidBorderColor,
    },

    // Invalid state - show helper text
    '& .field-node.invalid .field-helper-text-wrapper': {
      display: 'flex',
    },

    // Invalid state - helper text color
    '& .field-node.invalid .field-helper-text': {
      display: 'flex',
      color: helperTextInvalidColor,
    },

    // Invalid state - show error icon
    '& .field-node.invalid .field-input-icon': {
      display: 'inline-flex',
    },

    // While dragging in the editor, boundary indicators are shown via `.active-tree` on node DOMs.
    // Soften UI by hiding the error icon during drag to reduce visual noise.
    '& .active-tree .field-node.invalid .field-input-icon': {
      display: 'none',
      opacity: 0,
    },

    // Hidden fields and submit button
    '& .field-node--hidden, & .form-button-node--hidden': {
      display: 'none !important',
    },

    // Consent field specific styles
    '& .field-node--consent': {
      gap: '4px',
    },

    // Consent field description text
    '& .field-consent-description': {
      fontFamily: props.fieldLabelFontFamily,
      fontSize: props.fieldLabelFontSize,
      fontWeight: props.fieldLabelFontWeight,
      lineHeight: '1.5',
      color: labelColor,
      letterSpacing: `${props.fieldLabelSpacing}px`,
    },

    // Consent field link wrapper
    '& .field-link-wrapper': {
      display: 'inline-flex',
      marginBottom: '8px',
    },

    // Consent field link
    '& .field-link': {
      display: 'inline',
      color: labelColor,
      textDecoration: 'underline',
      fontFamily: props.fieldLabelFontFamily,
      fontSize: props.fieldLabelFontSize,
      fontWeight: props.fieldLabelFontWeight,
      lineHeight: '1.5',
      '&:hover': {
        textDecoration: 'none',
      },
    },

    // Consent checkbox wrapper
    '& .field-checkbox-wrapper': {
      display: 'flex',
      padding: '8px 0',
      alignItems: 'center',
      gap: '8px',
    },

    // Consent checkbox input - default state
    '& .field-checkbox': {
      width: `clamp(14px, calc(${props.fieldLabelFontSize || '10px'} * 1.6), 24px)`,
      height: `clamp(14px, calc(${props.fieldLabelFontSize || '10px'} * 1.6), 24px)`,
      minWidth: '14px',
      minHeight: '14px',
      flexShrink: 0,
      cursor: 'pointer',
      appearance: 'none',
      backgroundColor: bgColor,
      border: `1px solid ${borderColor}`,
      borderRadius: '4px',
      margin: 0,
      padding: 0,
      position: 'relative' as const,
      transition: 'background-color 150ms ease, border-color 150ms ease',
    },

    // Consent checkbox hover state (unchecked)
    '& .field-checkbox:hover:not(:checked)': {
      backgroundColor: hoverBgColor,
      borderColor: borderColor,
    },

    // Consent checkbox checked state
    '& .field-checkbox:checked': {
      backgroundColor: checkboxCheckedColor,
      borderColor: checkboxCheckedColor,
    },

    // Consent checkbox checkmark
    '& .field-checkbox:checked::after': {
      content: '""',
      position: 'absolute' as const,
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: '11px',
      height: '9px',
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='11' height='9' viewBox='0 0 11 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M10.1786 0.138323C10.3783 0.328993 10.3857 0.64549 10.195 0.845239L3.19501 8.17857C3.10206 8.27595 2.97376 8.33174 2.83915 8.3333C2.70453 8.33487 2.57497 8.28208 2.47978 8.18689L0.146447 5.85355C-0.0488155 5.65829 -0.0488155 5.34171 0.146447 5.14645C0.341709 4.95119 0.658291 4.95119 0.853553 5.14645L2.82502 7.11791L9.47166 0.154763C9.66232 -0.0449862 9.97882 -0.0523465 10.1786 0.138323Z' fill='white'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: 'contain',
    },

    // Consent checkbox text (label)
    '& .field-checkbox-text': {
      fontFamily: props.fieldLabelFontFamily,
      fontSize: props.fieldLabelFontSize,
      fontWeight: props.fieldLabelFontWeight,
      lineHeight: '1.5',
      color: labelColor,
      cursor: 'pointer',
      margin: 0,
    },

    // ====================================
    // Phone field (intl-tel-input) styles
    // ====================================

    // CSS Variables override for intl-tel-input theming
    '& .field-node--phone': {
      '--iti-hover-color': hoverBgColor,
      '--iti-border-color': borderColor,
      '--iti-dialcode-color': inputTextColor,
      '--iti-dropdown-bg': bgColor,
      '--iti-arrow-color': inputTextColor,
      '--iti-search-icon-color': inputTextColor,
      '--iti-spacer-horizontal': '10px',
      position: 'relative', // Needed for dropdown positioning
      overflow: 'visible !important', // Allow dropdown to overflow
      zIndex: 100, // Ensure dropdown appears above other elements
    },

    // Phone field wrapper needs special handling - serves as positioning context for dropdown
    '& .field-input-wrapper--phone': {
      padding: 0, // Remove padding from wrapper, let iti handle it
      overflow: 'visible !important',
      position: 'relative',
    },

    // Country selector container
    '& .field-node--phone .iti__country-container': {
      position: 'static !important',
      display: 'flex',
      alignItems: 'stretch',
      overflow: 'visible !important',
    },

    // Selected country primary wrapper - tighter spacing
    '& .field-node--phone .iti__selected-country-primary': {
      padding: '0 6px 0 12px',
      gap: '4px',
      display: 'flex',
      alignItems: 'center',
    },

    // Country selector button
    '& .field-node--phone .iti__selected-country': {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      height: '100%',
      outline: 'none',
      borderRadius: 0,
    },

    // Remove hover background effect from selected country
    '& .field-node--phone .iti__selected-country:hover .iti__selected-country-primary': {
      backgroundColor: 'transparent',
    },

    '& .field-node--phone .iti__selected-country-primary:hover': {
      backgroundColor: 'transparent !important',
    },

    // Override library default hover styles
    '& .field-node--phone .iti--allow-dropdown .iti__selected-country-primary:hover': {
      backgroundColor: 'transparent !important',
    },

    // Flag styling - ensure visibility on all states
    '& .field-node--phone .iti__flag': {
      width: '16px !important',
      height: '12px !important',
      borderRadius: '2px',
      boxShadow: '0 0 1px rgba(0, 0, 0, 0.2)',
      display: 'block !important',
      visibility: 'visible !important',
      opacity: '1 !important',
    },

    // Ensure flag stays visible on hover
    '& .field-node--phone .iti__selected-country:hover .iti__flag': {
      display: 'block !important',
      visibility: 'visible !important',
      opacity: '1 !important',
    },

    // Globe icon (shown when no country selected) - using custom icon
    '& .field-node--phone .iti__globe': {
      width: '20px',
      height: '20px',
      backgroundImage: 'none !important',
      border: 'none !important',
      boxShadow: 'none !important',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '16px',
        height: '16px',
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7.99902 0.833008C11.9571 0.833008 15.166 4.04196 15.166 8C15.1658 11.3561 12.858 14.1722 9.74316 14.9512L9.74219 14.9531L9.74023 14.9512C9.18266 15.0904 8.59971 15.166 7.99902 15.166C4.04115 15.166 0.832295 11.9578 0.832031 8C0.832031 4.04196 4.04098 0.833008 7.99902 0.833008ZM6.17383 2.10742C3.92435 2.80346 2.22593 4.75234 1.89258 7.13867C2.31246 7.44652 2.82496 7.67871 3.3916 7.67871C3.83533 7.67872 4.21781 7.69866 4.53906 7.75879C4.86103 7.8191 5.15724 7.92552 5.39746 8.12207C5.89761 8.53163 5.9707 9.17377 5.9707 9.83398C5.9707 10.513 5.97235 10.7991 6.00879 11.043C6.04374 11.2767 6.11205 11.4772 6.29004 11.9873C6.49143 12.5646 6.62646 13.2664 6.30566 13.9277C6.84395 14.0812 7.41149 14.166 7.99902 14.166C8.38649 14.166 8.76515 14.1279 9.13281 14.0596C9.47048 13.5202 9.62177 13.1103 9.67578 12.7939C9.73776 12.4307 9.67937 12.1583 9.58203 11.9111C9.53034 11.78 9.46801 11.657 9.39746 11.5176L9.39355 11.5088C9.32797 11.3792 9.24969 11.2256 9.19141 11.0664C9.05965 10.7064 9.02021 10.2949 9.26367 9.82812C9.43755 9.49489 9.68919 9.3004 9.99121 9.20508C10.2228 9.13203 10.4833 9.12091 10.6875 9.1123C10.7085 9.11142 10.7293 9.11125 10.749 9.11035C11.1987 9.08983 11.6886 9.05259 12.2891 8.62109C12.9373 8.15518 13.5881 8.0943 14.1592 8.22949C14.162 8.15331 14.166 8.07686 14.166 8C14.166 6.63641 13.722 5.37651 12.9727 4.35547C12.5388 4.45346 12.0865 4.67564 11.7529 5.10742C11.2112 5.80846 10.6303 6.23967 10.041 6.41309C9.44024 6.58987 8.87291 6.48457 8.41992 6.19141C7.74974 5.75746 7.66319 5.15112 7.60645 4.75391C7.60305 4.73011 7.59901 4.70678 7.5957 4.68457C7.56482 4.47698 7.53822 4.35128 7.48242 4.24805C7.43592 4.16205 7.35073 4.05901 7.14062 3.95703C6.58959 3.68944 6.28885 3.19213 6.19727 2.66406C6.16572 2.48214 6.15913 2.29488 6.17383 2.10742ZM14.041 9.23242C13.6696 9.11386 13.278 9.14168 12.873 9.43262C12.0365 10.0338 11.3115 10.0848 10.7949 10.1084V10.1094C10.5237 10.1218 10.3924 10.1275 10.292 10.1592C10.2382 10.1762 10.1994 10.1962 10.1504 10.29C10.0656 10.4525 10.0721 10.5623 10.1309 10.7227C10.1666 10.8203 10.2183 10.9237 10.29 11.0654L10.2949 11.0762C10.3615 11.2077 10.4421 11.3673 10.5117 11.5439C10.6607 11.9221 10.7612 12.3814 10.6621 12.9619C10.625 13.1794 10.5592 13.4079 10.4639 13.6514C12.2785 12.8589 13.6376 11.2201 14.041 9.23242ZM1.83887 8.2793C1.94343 10.6208 3.35389 12.6218 5.35938 13.5732C5.55742 13.2484 5.5337 12.8552 5.3457 12.3164C5.34124 12.3036 5.33641 12.2909 5.33203 12.2783C5.17124 11.8176 5.06987 11.5279 5.01953 11.1914C4.97066 10.8646 4.97067 10.4988 4.9707 9.88379C4.9707 9.86747 4.9707 9.85066 4.9707 9.83398C4.9707 9.17037 4.87096 8.98378 4.76465 8.89648C4.70167 8.84491 4.58562 8.78532 4.35547 8.74219C4.12453 8.69893 3.8139 8.67872 3.3916 8.67871C2.80757 8.67871 2.28508 8.512 1.83887 8.2793ZM7.99902 1.83301C7.73737 1.83301 7.47965 1.8502 7.22656 1.88184C7.1644 2.09505 7.15048 2.30783 7.18262 2.49316C7.22908 2.76105 7.36389 2.95398 7.57715 3.05762C7.95341 3.24025 8.2041 3.48182 8.36133 3.77246C8.50923 4.04607 8.55425 4.33068 8.58496 4.53711C8.58584 4.54302 8.58703 4.54887 8.58789 4.55469C8.65073 4.97738 8.67923 5.16833 8.96387 5.35254C9.17724 5.4905 9.44342 5.5469 9.75879 5.4541C10.0856 5.35793 10.5042 5.08844 10.9619 4.49609C11.3336 4.01505 11.7931 3.71431 12.25 3.53418C11.1437 2.48063 9.64737 1.83301 7.99902 1.83301Z' fill='%236B7280'/%3E%3C/svg%3E")`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      },
    },

    // Dial code display next to flag
    '& .field-node--phone .iti__selected-dial-code': {
      fontFamily: props.fieldInputFontFamily,
      fontSize: props.fieldInputFontSize,
      fontWeight: props.fieldInputFontWeight,
      color: inputTextColor,
      marginLeft: '4px',
    },

    // Arrow dropdown indicator
    '& .field-node--phone .iti__arrow': {
      marginLeft: '6px',
      borderLeft: '4px solid transparent',
      borderRight: '4px solid transparent',
      borderTop: `5px solid ${inputTextColor}`,
      opacity: 0.6,
    },

    '& .field-node--phone .iti__arrow--up': {
      borderTop: 'none',
      borderBottom: `5px solid ${inputTextColor}`,
    },

    // Phone input field inside iti
    '& .field-node--phone .field-input': {
      ...getPaddingStyle(props.fieldInputPadding || [6, 8, 6, 8]),
      paddingLeft: '0 !important',
    },

    // Ensure input takes remaining space
    '& .field-node--phone .iti input.iti__tel-input': {
      paddingLeft: '8px !important',
    },

    // ====================================
    // Dropdown styles (matching Figma design)
    // ====================================

    // Make iti container the positioning context for dropdown
    '& .field-node--phone .iti': {
      width: '100%',
      display: 'flex',
      alignItems: 'stretch',
      overflow: 'visible !important',
      position: 'relative !important', // This is the positioning context
    },

    // Dropdown container - positioned relative to .iti (full width of input)
    '& .field-node--phone .iti__dropdown-content': {
      position: 'absolute !important',
      top: '100% !important',
      left: '0 !important',
      right: 'auto !important',
      width: '100% !important',
      marginTop: '4px !important',
      marginLeft: '0 !important',
      backgroundColor: bgColor,
      ...getBorderRadiusStyle(props.fieldBorderRadius || [6, 6, 6, 6]),
      boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
      border: `1px solid ${borderColor}`,
      overflow: 'visible !important',
      zIndex: '9999 !important',
      padding: '8px',
    },

    // Search input wrapper
    '& .field-node--phone .iti__search-input-wrapper': {
      position: 'relative',
      marginBottom: '8px',
    },

    // Search input field - override default styles
    '& .field-node--phone .iti__search-input': {
      display: 'block',
      width: '100%',
      boxSizing: 'border-box',
      fontFamily: props.fieldInputFontFamily,
      fontSize: props.fieldLabelFontSize || '14px',
      fontWeight: props.fieldLabelFontWeight || 400,
      height: '40px',
      padding: '10px 36px 10px 12px',
      paddingLeft: '12px !important',
      border: `1px solid ${borderColor} !important`,
      borderRadius: '8px !important',
      outline: 'none',
      backgroundColor: `${bgColor} !important`,
      color: inputTextColor,
      backgroundImage: 'none !important',
    },

    '& .field-node--phone .iti__search-input::placeholder': {
      color: inputTextColor,
      opacity: 0.5,
    },

    '& .field-node--phone .iti__search-input:focus': {
      outline: 'none',
      borderColor: `${checkboxCheckedColor} !important`,
    },

    // Search icon - positioned inside wrapper
    '& .field-node--phone .iti__search-icon': {
      display: 'flex',
      position: 'absolute',
      right: '10px',
      left: 'auto',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '18px',
      height: '18px',
      pointerEvents: 'none',
    },

    '& .field-node--phone .iti__search-icon-svg': {
      width: '100%',
      height: '100%',
      stroke: inputTextColor,
      opacity: 0.4,
    },

    // Clear search button
    '& .field-node--phone .iti__search-clear': {
      right: '32px',
      width: '24px',
      height: '24px',
      backgroundColor: 'transparent',
    },

    '& .field-node--phone .iti__search-clear-svg': {
      fill: inputTextColor,
      opacity: 0.6,
    },

    '& .field-node--phone:hover .iti__search-clear-svg': {
      fill: inputTextColor,
      opacity: 0.6,
    },

    // No results message
    '& .field-node--phone .iti__no-results': {
      textAlign: 'center',
      padding: '20px 10px',
      fontFamily: props.fieldInputFontFamily,
      fontSize: props.fieldLabelFontSize || '14px',
      color: inputTextColor,
      opacity: 0.6,
    },

    // Remove border between search and list
    '& .field-node--phone .iti__search-input + .iti__country-list': {
      borderTop: 'none',
    },

    // Country list container
    '& .field-node--phone .iti__country-list': {
      maxHeight: '200px',
      overflowY: 'auto',
      overflowX: 'hidden',
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },

    // Custom scrollbar for country list
    '& .field-node--phone .iti__country-list::-webkit-scrollbar': {
      width: '6px',
    },
    '& .field-node--phone .iti__country-list::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '& .field-node--phone .iti__country-list::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
      borderRadius: '3px',
    },

    // Individual country item
    '& .field-node--phone .iti__country': {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      minHeight: '36px',
      padding: '8px 10px',
      paddingRight: '32px', // Space for checkmark
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'background-color 100ms ease',
      backgroundColor: 'transparent',
      position: 'relative',
    },

    '& .field-node--phone .iti__country:hover': {
      backgroundColor: hoverBgColor,
    },

    // Highlighted country (keyboard navigation)
    '& .field-node--phone .iti__country.iti__highlight': {
      backgroundColor: hoverBgColor,
    },

    // Active/selected country - add checkmark
    '& .field-node--phone .iti__country.iti__active': {
      backgroundColor: 'transparent',
    },

    '& .field-node--phone .iti__country.iti__active::after': {
      content: '""',
      position: 'absolute',
      right: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '16px',
      height: '16px',
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.3334 4L6.00008 11.3333L2.66675 8' stroke='%234F46E5' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: 'contain',
    },

    '& .field-node--phone .iti__country.iti__active .iti__country-name': {
      color: checkboxCheckedColor,
      fontWeight: 500,
    },

    '& .field-node--phone .iti__country.iti__active .iti__dial-code': {
      color: checkboxCheckedColor,
    },

    // Flag in country list
    '& .field-node--phone .iti__country .iti__flag': {
      width: '16px !important',
      height: '12px !important',
      borderRadius: '2px',
      flexShrink: 0,
    },

    // Country name text
    '& .field-node--phone .iti__country-name': {
      flex: 1,
      fontFamily: props.fieldInputFontFamily,
      fontSize: props.fieldLabelFontSize || '14px',
      fontWeight: props.fieldLabelFontWeight || 400,
      lineHeight: '20px',
      color: inputTextColor,
      marginRight: 0,
    },

    // Dial code in country list
    '& .field-node--phone .iti__dial-code': {
      fontFamily: props.fieldInputFontFamily,
      fontSize: props.fieldLabelFontSize || '14px',
      fontWeight: props.fieldLabelFontWeight || 400,
      lineHeight: '20px',
      color: inputTextColor,
      opacity: 0.5,
    },

    // Divider (dashed line) - appears after preferred countries
    '& .field-node--phone .iti__divider': {
      borderTop: `1px dashed ${borderColor}`,
      margin: '6px 0',
      opacity: 0.5,
    },

    // Hide error icon inside phone wrapper (it's positioned differently)
    '& .field-input-wrapper--phone .field-input-icon': {
      right: '12px',
    },

    // ====================================
    // Consent field styles
    // ====================================

    // Invalid state - consent checkbox
    '& .field-node--consent.invalid .field-checkbox': {
      backgroundColor: invalidBgColor,
      borderColor: invalidBorderColor,
    },

    // Invalid state - consent helper text wrapper (position on left under checkbox)
    '& .field-node--consent.invalid .field-helper-text-wrapper': {
      display: 'flex',
      justifyContent: 'flex-start',
      marginTop: '0px',
      marginLeft: '0px',
    },

    // Invalid state - hide error icon for consent fields
    '& .field-node--consent.invalid .field-input-icon': {
      display: 'none',
    },

    // ===========================================
    // Selection field specific styles (checkbox/radio)
    // ===========================================

    // Selection field wrapper - matches Figma container with subtle border
    '& .field-node--selection': {
      padding: '6px 4px',
      borderRadius: '6px',
      gap: '4px',
    },

    // Selection placeholder text
    '& .field-selection-placeholder': {
      fontFamily: props.fieldLabelFontFamily,
      fontSize: props.fieldLabelFontSize,
      fontWeight: props.fieldLabelFontWeight,
      lineHeight: '1.5',
      color: labelColor,
      letterSpacing: `${props.fieldLabelSpacing}px`,
      margin: 0,
    },

    // Selection options wrapper
    '& .field-selection-wrapper': {
      display: 'flex',
      flexDirection: 'column' as const,
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      width: '100%',
    },

    // Selection option row
    '& .field-selection-option': {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      width: '100%',
      height: '32px',
      padding: '8px 0',
      borderRadius: '8px',
      cursor: 'pointer',
    },

    // Selection input (checkbox) - default state per Figma
    '& .field-selection-input': {
      width: `clamp(14px, calc(${props.fieldLabelFontSize || '10px'} * 1.6), 24px)`,
      height: `clamp(14px, calc(${props.fieldLabelFontSize || '10px'} * 1.6), 24px)`,
      minWidth: '14px',
      minHeight: '14px',
      flexShrink: 0,
      cursor: 'pointer',
      appearance: 'none',
      backgroundColor: bgColor,
      border: `1px solid ${borderColor}`,
      borderRadius: '4px',
      margin: 0,
      padding: 0,
      position: 'relative' as const,
      transition: 'background-color 150ms ease, border-color 150ms ease, border-width 150ms ease',
    },

    // Selection input hover state (unchecked) per Figma
    '& .field-selection-input:hover:not(:checked)': {
      backgroundColor: hoverBgColor,
      borderColor: borderColor,
    },

    // Selection input checked/active state per Figma (checkbox)
    '& .field-selection-input:checked': {
      backgroundColor: checkboxCheckedColor,
      borderColor: checkboxCheckedColor,
    },

    // Selection input checkmark
    '& .field-selection-input:checked::after': {
      content: '""',
      position: 'absolute' as const,
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: '10px',
      height: '8px',
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='11' height='9' viewBox='0 0 11 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M10.1786 0.138323C10.3783 0.328993 10.3857 0.64549 10.195 0.845239L3.19501 8.17857C3.10206 8.27595 2.97376 8.33174 2.83915 8.3333C2.70453 8.33487 2.57497 8.28208 2.47978 8.18689L0.146447 5.85355C-0.0488155 5.65829 -0.0488155 5.34171 0.146447 5.14645C0.341709 4.95119 0.658291 4.95119 0.853553 5.14645L2.82502 7.11791L9.47166 0.154763C9.66232 -0.0449862 9.97882 -0.0523465 10.1786 0.138323Z' fill='white'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: 'contain',
    },

    // Radio button specific styles (circular) - default state
    '& .field-selection-input[type="radio"]': {
      borderRadius: '50%',
      backgroundColor: bgColor,
      border: `1px solid ${borderColor}`,
    },

    // Radio button hover state (unchecked)
    '& .field-selection-input[type="radio"]:hover:not(:checked)': {
      backgroundColor: hoverBgColor,
      borderColor: borderColor,
    },

    // Radio button checked/active state per Figma - white bg with thick indigo border
    '& .field-selection-input[type="radio"]:checked': {
      backgroundColor: 'white',
      border: `4px solid ${checkboxCheckedColor}`,
    },

    // Radio button checked - no inner dot needed, thick border creates the effect
    '& .field-selection-input[type="radio"]:checked::after': {
      display: 'none',
    },

    // Selection option text (label)
    '& .field-selection-text': {
      flex: '1 1 0',
      fontFamily: props.fieldLabelFontFamily,
      fontSize: props.fieldLabelFontSize,
      fontWeight: props.fieldLabelFontWeight,
      lineHeight: '1.5',
      color: labelColor,
      letterSpacing: `${props.fieldLabelSpacing}px`,
      cursor: 'pointer',
      margin: 0,
    },

    // ===================================
    // DROPDOWN FIELD STYLES
    // ===================================

    // Dropdown node wrapper
    '& .field-node--dropdown': {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px',
    },

    // Dropdown wrapper (contains trigger + popover)
    '& .field-dropdown-wrapper': {
      position: 'relative' as const,
      width: '100%',
    },

    // Dropdown trigger button (matches Figma design; uses Form layout props for padding/corners and stroke)
    '& .field-dropdown-trigger': {
      width: '100%',
      height: 'auto',
      display: 'inline-flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      ...getPaddingStyle(props.fieldInputPadding || [6, 8, 6, 8]),
      backgroundColor: bgColor,
      border: `${props.fieldInputBorderWidth ?? 1}px solid ${borderColor}`,
      ...getBorderRadiusStyle(props.fieldBorderRadius || [8, 8, 8, 8]),
      boxShadow: getBoxShadow(props.fieldShadowType, props.fieldShadowSettings) ?? 'none',
      cursor: 'pointer',
      transition: 'border-color 150ms ease, background-color 150ms ease',

      '&:hover': {
        backgroundColor: hoverBgColor,
        borderColor: hoverBorderColor,
      },

      '&:focus': {
        outline: 'none',
        borderColor: hoverBorderColor,
      },
    },

    // Dropdown trigger open state (same as hover for consistency with other inputs)
    '& .field-dropdown-trigger--open': {
      borderColor: hoverBorderColor,
    },

    // Dropdown trigger text (uses Form field text props like .field-input)
    '& .field-dropdown-trigger-text': {
      display: 'flex',
      flexDirection: 'column' as const,
      justifyContent: 'center',
      fontFamily: props.fieldInputFontFamily,
      fontSize: props.fieldInputFontSize,
      fontWeight: props.fieldInputFontWeight,
      letterSpacing: `${props.fieldInputSpacing}px`,
      lineHeight: '16px',
      color: inputTextColor,
      transition: 'color 150ms ease',
    },

    // Placeholder text styling (uses same text color as other inputs)
    '& .field-dropdown-trigger--placeholder .field-dropdown-trigger-text': {
      color: inputTextColor,
    },

    // Trigger text hover/focus/open
    '& .field-dropdown-trigger:hover .field-dropdown-trigger-text': {
      color: hoverTextColor || inputTextColor,
    },
    '& .field-dropdown-trigger:focus .field-dropdown-trigger-text': {
      color: hoverTextColor || inputTextColor,
    },
    '& .field-dropdown-trigger--open .field-dropdown-trigger-text': {
      color: hoverTextColor || inputTextColor,
    },

    // Chevron icon in trigger (same color as text)
    '& .field-dropdown-chevron': {
      width: '16px',
      height: '16px',
      color: inputTextColor,
      flexShrink: 0,
      transition: 'transform 150ms ease, color 150ms ease',
    },

    // Chevron color on hover/focus/open (matches text hover)
    '& .field-dropdown-trigger:hover .field-dropdown-chevron': {
      color: hoverTextColor || inputTextColor,
    },
    '& .field-dropdown-trigger:focus .field-dropdown-chevron': {
      color: hoverTextColor || inputTextColor,
    },
    '& .field-dropdown-trigger--open .field-dropdown-chevron': {
      color: hoverTextColor || inputTextColor,
      transform: 'rotate(180deg)',
    },

    // Dropdown popover (options list)
    '& .field-dropdown-popover': {
      position: 'absolute' as const,
      top: 'calc(100% + 10px)', // 10px gap per Figma
      left: 0,
      width: '100%',
      minWidth: '180px',
      padding: '4px',
      backgroundColor: 'white',
      boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.10)',
      ...getBorderRadiusStyle([6, 6, 6, 6]),
      outline: '1px solid #E9ECF0', // neutral-100 per Figma
      outlineOffset: '-1px',
      display: 'inline-flex',
      flexDirection: 'column' as const,
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      zIndex: 1000,
      overflow: 'hidden',
    },

    // Dropdown option item (default state)
    '& .field-dropdown-option': {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '32px',
      padding: '8px',
      ...getBorderRadiusStyle([8, 8, 8, 8]),
      cursor: 'pointer',
      transition: 'background-color 150ms ease',
    },

    // Dropdown option hover state (keyboard navigation only)
    '& .field-dropdown-option--hover': {
      backgroundColor: '#E9ECF0', // neutral-100 per Figma
      borderRadius: '4px',
    },

    // Dropdown option text
    '& .field-dropdown-option-text': {
      flex: 1,
      fontSize: '12px',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 500,
      lineHeight: '16px',
      color: '#575F6A', // neutral-500 per Figma
    },

    // Selected option text color
    '& .field-dropdown-option--selected .field-dropdown-option-text': {
      color: '#6245F2', // indigo-500 per Figma
    },

    // Checkmark icon for selected option
    '& .field-dropdown-checkmark': {
      width: '16px',
      height: '16px',
      color: '#6245F2', // indigo-500 per Figma
      flexShrink: 0,
    },

    '& .field-dropdown-option:has(.field-dropdown-checkbox)': {
      justifyContent: 'flex-start',
      gap: '8px',
    },

    // Checkbox styles for multi-select mode (matches @/components/ui/checkbox)
    '& .field-dropdown-checkbox': {
      width: '16px',
      height: '16px',
      backgroundColor: 'white',
      borderRadius: '4px',
      border: '1px solid #CED1D6', // neutral-200 (matches border-input)
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', // shadow-xs
      flexShrink: 0,
      position: 'relative' as const,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    '& .field-dropdown-checkbox--checked': {
      backgroundColor: '#6245F2', // indigo-500
      borderColor: '#6245F2',
    },

    // Tick icon inside checkbox (SVG icon component)
    // Hidden by default, shown when checkbox is checked
    '& .field-dropdown-checkbox-icon': {
      width: '16px',
      height: '16px',
      color: 'white',
      display: 'none',
    },

    '& .field-dropdown-checkbox--checked .field-dropdown-checkbox-icon': {
      display: 'block',
    },

    // Multi-select trigger styles
    '& .field-dropdown-trigger--multiselect': {
      height: '32px',
      paddingTop: '4px',
      paddingBottom: '4px',
      paddingLeft: '4px',
      paddingRight: '8px',
      gap: '4px',
    },

    // Tags container for multi-select (single line, no wrap)
    '& .field-dropdown-tags': {
      display: 'flex',
      flexWrap: 'nowrap' as const,
      gap: '4px',
      flex: 1,
      alignItems: 'center',
      overflow: 'hidden',
    },

    // Individual tag for selected value (fit-content, no forced truncate)
    '& .field-dropdown-tag': {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      height: '24px',
      paddingTop: '2px',
      paddingBottom: '2px',
      paddingLeft: '8px',
      paddingRight: '2px',
      backgroundColor: '#E9ECF0', // neutral-100
      borderRadius: '4px',
      flexShrink: 0,
    },

    '& .field-dropdown-tag-text': {
      fontSize: '12px',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 500,
      lineHeight: '16px',
      color: '#575F6A', // neutral-500
      whiteSpace: 'nowrap' as const,
    },

    // "+N more" badge for when more than one item is selected
    '& .field-dropdown-tag-more': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '24px',
      paddingLeft: '8px',
      paddingRight: '8px',
      backgroundColor: '#E9ECF0', // neutral-100
      borderRadius: '4px',
      fontSize: '12px',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 500,
      lineHeight: '16px',
      color: '#575F6A', // neutral-500
      flexShrink: 0,
    },

    '& .field-dropdown-tag-remove': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '20px',
      height: '20px',
      padding: 0,
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      borderRadius: '4px',
      transition: 'background-color 150ms ease',

      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
      },
    },

    '& .field-dropdown-tag-remove-icon': {
      width: '14px',
      height: '14px',
      color: '#575F6A', // neutral-500
    },
  };
}
